<template>
  <div class="home">
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans&family=Roboto+Slab&display=swap" rel="stylesheet">
    <!-- Add StructuredData component -->
    <StructuredData :type="'TravelAgency'" :data="schemaData" />
    
    <section class="hero">
      <div class="hero-content">
        <router-link to="/tours" class="hero-title">
          <h1>The Real Yunnan</h1>
        </router-link>
      </div>
    </section>
    
    <section class="service-features">
      <div class="features-grid">
        <div class="feature-item">
          <font-awesome-icon icon="check-circle" class="feature-icon" />
          <h3>100% Tailor-made for Free</h3>
        </div>
        <div class="feature-item">
          <font-awesome-icon icon="check-circle" class="feature-icon" />
          <h3>Exclusive Yunnan Itinerary</h3>
        </div>
        <div class="feature-item">
          <font-awesome-icon icon="check-circle" class="feature-icon" />
          <h3>One-one Expert Consultant</h3>
        </div>
        <div class="feature-item">
          <font-awesome-icon icon="check-circle" class="feature-icon" />
          <h3>Guarantee No Shopping Tour</h3>
        </div>
        <div class="feature-item">
          <font-awesome-icon icon="check-circle" class="feature-icon" />
          <h3>Focused on Yunnan</h3>
        </div>
        <div class="feature-item">
          <font-awesome-icon icon="check-circle" class="feature-icon" />
          <h3>Experienced Yunnan Local Guide</h3>
        </div>
      </div>
    </section>
    
    <section class="best-tours">
      <div class="section-header">
        <h2 class="decorated-title"><i><b><u>Best Private Yunnan Tours</u></b></i></h2>
      </div>
      
      <div class="tours-grid">
        <router-link 
          v-for="tour in recommendedTours" 
          :key="tour.id"
          :to="`/tours/${tour.category}/${tour.id}`" 
          class="tour-card"
        >
          <img :src="tour.image" :alt="tour.title">
          <div class="tour-info">
            <h3 class="tour-title">{{ tour.title }}</h3>
            <p class="tour-subtitle">{{ tour.subtitle }}</p>
          </div>
        </router-link>
      </div>
      <p class="section-footer-text">Embark on an unforgettable journey through Yunnan, China's most diverse province. Our curated Yunnan tours offer authentic experiences, blending natural beauty with cultural immersion.Discover the authentic beauty of Yunnan with Real Yunnan Travel, your premier partner for customized private tours.</p>
    </section>
    
    <section class="yunnan-video">
      <div class="section-header">
        <h2 class="decorated-title"><i><b><u>Yunnan Tours</u></b></i></h2>
        <p>Experience the beauty and culture of Yunnan</p>
      </div>
      
      <div class="video-content">
        <div class="video-container">
          <iframe 
            src="https://www.youtube.com/embed/DImRuEPLSbE?si=Cag5bw9eCKzJPYOc" 
            title="Yunnan Travel Video"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen>
          </iframe>
        </div>
        <div class="video-text">
          <h3>Why Choose Yunnan?</h3>
          <p>
            Yunnan is a province in southwestern China known for its diverse ethnic cultures, stunning landscapes, and mild climate.
            It borders Myanmar, Laos, and Vietnam, making it a culturally rich and historically significant region.
            Lonely Planet have described Yunnan as "If you have time for but one place to visit in China, it must be Yunnan."
          </p>
          <p>
            From the bustling city of Kunming to the spiritual haven of Shangri-La, we offer a range of experiences to suit every traveler. 
            Explore the iconic Stone Forest, hike through the picturesque rice terraces of Yuanyang, and discover the vibrant markets of Jianshui. 
            Whether you're seeking adventure, cultural exploration, or relaxation, our Yunnan tours provide the perfect gateway to this enchanting region. 
            Experience the best of Yunnan travel with us!
          </p>
        </div>
      </div>
    </section>
    
    <section class="popular-destinations">
      <div class="section-header">
        <h2 class="decorated-title"><i><b><u>The Most Popular Yunnan Tour Destinations</u></b></i></h2>
        <p>Discover the must-visit places in Yunnan</p>
      </div>
      
      <div class="destinations-grid">
        <router-link 
          v-for="destination in destinations" 
          :key="destination.id" 
          :to="`/destination/${destination.id}`"
          class="destination-card"
        >
          <img :src="destination.image" :alt="destination.name">
          <div class="destination-info">
            <h3>{{ destination.name }}</h3>
            <p>{{ destination.brief }}</p>
          </div>
        </router-link>
      </div>
    </section>
    
    <TestimonialsSection />
    <TravelerMemories :altBackground="false" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { tourData } from '../data/tourData';
import { cities } from '../data/cityData';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import TestimonialsSection from '../components/TestimonialsSection.vue';
import TravelerMemories from '../components/TravelerMemories.vue';
import { getCurrentInstance } from 'vue';
import StructuredData from '../components/StructuredData.vue';

// Schema.org structured data
const schemaData = {
  name: 'Real Yunnan Tours',
  description: 'Professional travel agency specializing in Yunnan tours and experiences',
  url: 'https://realyunnan.com/',
  logo: 'https://realyunnan.com/images/logo.jpg',
  email: 'info@realyunnan.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Kunming',
    addressRegion: 'Yunnan',
    postalCode: '650000',
    addressCountry: 'CN'
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '25.0454',
    longitude: '102.7091'
  },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    opens: '08:00',
    closes: '18:00'
  },
  priceRange: '$$'
};

// Get access to meta plugin
const { proxy } = getCurrentInstance();

// Setup meta tags when component is mounted
onMounted(() => {
  // Set meta tags for this page
  proxy.$meta.update({
    title: 'Discover the Magic of Yunnan - China\'s Most Diverse Province',
    description: 'Explore Yunnan with our professional tour guides. Visit Kunming, Dali, Lijiang, Shangri-La and experience authentic local culture and breathtaking landscapes.',
    image: '/images/hero-yunnan.jpg',
    canonicalUrl: 'https://realyunnan.com/'
  });
});

// Get featured tours from each category for recommendation
const recommendedTours = ref([
  {
    ...tourData.ClassicTour.find(tour => tour.id === 'best-of-yunnan'),
    category: 'ClassicTour'
  },
  {
    ...tourData.ClassicTour.find(tour => tour.id === 'highlights-of-yunnan'),
    category: 'ClassicTour'
  },
  {
    ...tourData.ClassicTour.find(tour => tour.id === 'discover-real-yunnan'),
    category: 'ClassicTour'
  }
]);

// Get popular destinations
const destinations = ref(cities.slice(0, 8));

function getCityCategory(id) {
  const categoryMap = {
    'kunming': 'Kunming',
    'lijiang': 'Lijiang',
    'dali': 'Dali',
    'yuanyang': 'Yuanyang',
    'shangri-la': 'ShangriLa',
    'puer': 'Puer',
    'jianshui': 'Jianshui',
    'deqin': 'Deqin'
  };
  return categoryMap[id] || id;
}
</script>

<style scoped>
.home {
  width: 100%;
}

.hero {
  min-height: 450px;
  background-image: url('/images/hero-yunnan.jpg');
  background-size: cover;
  background-position: center;
  color: white;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  text-align: left;
  position: relative;
}

.hero-content {
  max-width: 900px;
  padding: 2rem;
  position: relative;
  z-index: 1;
  margin-left: 5rem;
  margin-bottom: 7rem;
}

.hero-title {
  text-decoration: none;
  color: white;
  display: inline-block;
}

.hero-title h1 {
  font-size: 4.0rem;
  font-family: Serif;
  margin: 0;
  background-color: rgb(0 0 0 / 27%);
  padding: 1rem 2rem;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.hero-title:hover {
  background-color: transparent;
}

.hero-title:hover h1 {
  background-color: rgb(0 0 0 / 27%);
  transform: translateY(-3px);
}

.btn {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-primary {
  background-color: #008c5c;
  color: white;
}

.btn-primary:hover {
  background-color: #00734b;
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-header h2 {
  font-size: 3.5rem;
  color: var(--color-heading);
  margin-bottom: 1rem;
}

.section-header p {
  font-size: 1.5rem;
  color: var(--color-text);
  margin: 0 auto;
}

/* Service Features Section */
.service-features {
  padding: 1.5rem 1.5rem;
  background-color: #f8f8f8;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  max-width: 1200px;
  margin: 0 auto;
}

.feature-item {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.feature-icon {
  color: #008c5c;
  font-size: 1.2rem;
}

.feature-item:hover {
  transform: translateY(-5px);
}

.feature-item h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #44464a;
}

/* Decorated title for sections */
.decorated-title {
  display: inline-block;
  position: relative;
  font-weight: 800;
  font-style: italic;
  text-decoration: underline;
  font-family: Serif;
  color: var(--color-heading);
}

/* Best Tours Section */
.best-tours {
  padding: 3rem 2rem;
  max-width: 1500px;
  margin: 0 auto;
}

.tours-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.1rem;
  margin: 0 auto;
}

.tour-card {
  background: var(--color-background);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  text-decoration: none;
  color: inherit;
  display: block;
}

.tour-card:hover {
  transform: translateY(-10px);
}

.tour-card img {
  width: 100%;
  height: 350px;
  object-fit: cover;
}

.tour-info {
  padding: 1rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
}

.tour-title {
  color: #120404;
  font-size: 1.3rem;
  font-weight: 400;
}

.tour-subtitle {
  color: white;
  background-color: #524246;
  padding: 0.6rem 0.6rem;
  border-radius: 1.5rem;
  font-weight: bold;
  font-style: italic;
  margin-bottom: 0.2rem;
  display: inline-block;
  font-size: 0.9rem;
  max-width: 90%;
  word-wrap: break-word;
}

.tour-meta {
  display: flex;
  justify-content: center;
  color: #000000;
  margin-bottom: 0.2rem;
}

.tour-meta span {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.btn-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  padding: 0 1.5rem;
}

.btn {
  display: inline-block;
  padding: 0.4rem 1rem;
  border-radius: 4px;
  background-color: #000000;
  color: white;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.btn:hover {
  background-color: #333333;
  transform: translateY(-2px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
}

.arrow-icon {
  position: absolute;
  font-size: 1.2rem;
  color: #000000;
  top: 50%;
  transform: translateY(-50%);
}

.arrow-icon.left {
  left: 0;
}

.arrow-icon.right {
  right: 0;
}

/* Yunnan Video Section */
.yunnan-video {
  padding: 2rem 1rem;
  background-color: #f8f8f8b8;
}

.video-content {
  display: grid;
  grid-template-columns: 1.5fr 1fr; /* fraction 1.5fr 1fr → 视频比文字大一些 */
  gap: 3rem;
  max-width: 1200px;
  margin: 0 auto;
  align-items: center;
}

.video-container {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  height: 0;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.video-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.video-text h3 {
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: var(--color-heading);
}

.video-text p {
  margin-bottom: 1.5rem;
  line-height: 1.6;
  color: var(--color-text);
}

/* Popular Destinations Section */
.popular-destinations {
  padding: 3rem 2rem;
}

.destinations-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.1rem;
  max-width: 1500px;
  margin: 0 auto;
}

.destination-card {
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.destination-card:hover {
  transform: translateY(-10px);
}

.destination-card a {
  text-decoration: none;
  color: inherit;
}

.destination-card img {
  display: block;
  width: 100%;
  height: 220px;
  object-fit: cover;
}

.destination-info {
  padding: 1.5rem;
  background: var(--color-background);
}

.destination-info h3 {
  margin: 0 0 0.5rem 0;
  color: var(--color-heading);
}

.destination-info p {
  margin: 0;
  color: var(--color-text);
  font-size: 0.9rem;
}

/* 修改红色背景样式，只针对标题 */
.title-red-background :deep(.decorated-title) {
  background-color: #8a0707;
  padding: 1rem;
  font-size: 4rem;
  color: black !important;
  margin-bottom: 1rem;
}

/* Add styles for the moved paragraph */
.section-footer-text {
  font-size: 1.1rem;
  color: var(--color-text);
  max-width: 800px; /* Adjust this to control line length */
  margin: 3rem auto 0; /* Add space above, center horizontally */
  text-align: center;
  line-height: 1.6; /* Adjust for readability */
}

@media (max-width: 1024px) {
  .destinations-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .tours-grid {
    grid-template-columns: 1fr; /* 小屏时一列一张卡片 */
  }
  
  .video-content {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 768px) {
  .hero {
    min-height: 300px;
  }
  
  .hero h1 {
    font-size: 1.8rem;
    margin: 0 0;
  }
  
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .destinations-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .hero-content {
    padding: 0rem;
    margin-left: 1rem;
    margin-bottom: 2rem;
  }

  .feature-item {
    padding: 0.8rem;
    flex-direction: row;
    justify-content: flex-start;
    text-align: left;
  }

  /* Adjust footer text for mobile */
  .section-footer-text {
      max-width: 90%; /* Allow slightly wider on mobile */
      margin-top: 2rem; /* Adjust top margin */
      font-size: 1rem; /* Slightly smaller text */
  }
}

@media (max-width: 480px) {
  .hero {
    min-height: 200px;
  }
  
  .service-features {
    display: none;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
  }
  
  .destinations-grid {
    grid-template-columns: 1fr;
    gap: 1rem;  /* Reduce gap between cards */
  }

  /* Reduce padding for all major sections */
  .best-tours,
  .popular-destinations {
    padding: 1.5rem 1rem;  /* Reduce from 3rem 2rem */
  }

  .yunnan-video {
    padding: 1rem 0;  /* Keep video edge-to-edge */
  }
  
  .video-content {
    margin: 0;  /* Remove margin */
  }
  
  .video-container {
    border-radius: 0;  /* Remove border radius */
  }
  
  /* Adjust video section spacing */
  .video-text {
    padding: 1rem 0.5rem;  /* Reduce padding */
  }
  
  /* Reduce gap between sections */
  .section-header {
    margin-bottom: 1rem;  /* Adjust from 0.5rem to 1rem */
  }
  
  .section-header p {
    padding: 0 1.5rem;  /* Add padding for better readability */
    margin-top: 0.75rem;  /* Add some space after title */
    font-size: 1.1rem;  /* Slightly reduce font size for mobile */
  }
  
  .section-header h2.decorated-title {
    font-size: 1.8rem;  /* Reduce font size */
    line-height: 1.3;  /* Add line height for better readability when wrapped */
  }

  /* Adjust grid gaps */
  .tours-grid {
    gap: 1rem;  /* Reduce gap between cards */
  }

  .section-footer-text {
    font-size: 0.95rem;
  }
}
</style> 